import { apiRoute } from "./config";

export type MarkPromptReviewState =
  | "unreviewed"
  | "triaged"
  | "actioned"
  | "duplicate"
  | "no_action"
  | "obsolete";
export type MarkPromptLinkKind =
  | "issue"
  | "pull_request"
  | "task"
  | "other";
export type MarkPromptLinkStatus =
  | "open"
  | "closed"
  | "draft"
  | "merged"
  | "active"
  | "complete"
  | "unknown";

export interface MarkPromptLink {
  kind: MarkPromptLinkKind;
  label: string;
  url: string;
  status: MarkPromptLinkStatus;
  githubNumber: number | null;
  updatedAt: string;
}

export interface MarkPrompt {
  id: string;
  occurredAt: string;
  summary: string;
  abbreviatedText: string;
  fullText: string;
  reviewState: MarkPromptReviewState;
  resultSummary: string | null;
  duplicateOfPromptID: string | null;
  source: {
    taskID: string;
    taskTitle: string;
  };
  links: MarkPromptLink[];
}

export interface MarkPromptSource {
  taskID: string;
  taskTitle: string;
  count: number;
  firstOccurredAt: string;
  lastOccurredAt: string;
}

export type MarkPromptOrder = "asc" | "desc";

export interface MarkPromptDay {
  date: string;
  count: number;
}

export interface MarkPromptsPageData {
  stats: {
    total: number;
    byDay: MarkPromptDay[];
  };
  sources: MarkPromptSource[];
  order: MarkPromptOrder;
  prompts: MarkPrompt[];
  nextOffset: number | null;
}

export interface MarkPromptsRepository {
  load(options?: {
    offset?: number;
    limit?: number;
    taskIDs?: string[];
    order?: MarkPromptOrder;
  }): Promise<MarkPromptsPageData>;
}

export class MarkPromptsRepositoryError extends Error {
  constructor(
    readonly status: number | null,
    message = "mark_prompts_unavailable",
  ) {
    super(message);
  }
}

const reviewStates = new Set<MarkPromptReviewState>([
  "unreviewed",
  "triaged",
  "actioned",
  "duplicate",
  "no_action",
  "obsolete",
]);
const linkKinds = new Set<MarkPromptLinkKind>([
  "issue",
  "pull_request",
  "task",
  "other",
]);
const linkStatuses = new Set<MarkPromptLinkStatus>([
  "open",
  "closed",
  "draft",
  "merged",
  "active",
  "complete",
  "unknown",
]);

function object(value: unknown): value is Record<string, unknown> {
  return Boolean(value) && !Array.isArray(value) && typeof value === "object";
}

function exactKeys(
  value: Record<string, unknown>,
  keys: readonly string[],
): boolean {
  return Object.keys(value).sort().join("\u0000")
    === [...keys].sort().join("\u0000");
}

function boundedString(
  value: unknown,
  maximum: number,
): value is string {
  return typeof value === "string"
    && value.length > 0
    && value.length <= maximum;
}

function timestamp(value: unknown): value is string {
  return boundedString(value, 40) && Number.isFinite(Date.parse(value));
}

function link(value: unknown): MarkPromptLink {
  if (
    !object(value)
    || !exactKeys(value, [
      "kind",
      "label",
      "url",
      "status",
      "github_number",
      "updated_at",
    ])
    || typeof value.kind !== "string"
    || !linkKinds.has(value.kind as MarkPromptLinkKind)
    || !boundedString(value.label, 240)
    || !boundedString(value.url, 1000)
    || typeof value.status !== "string"
    || !linkStatuses.has(value.status as MarkPromptLinkStatus)
    || (
      value.github_number !== null
      && (
        typeof value.github_number !== "number"
        || !Number.isSafeInteger(value.github_number)
        || value.github_number < 1
      )
    )
    || !timestamp(value.updated_at)
  ) {
    throw new Error("invalid_mark_prompts_payload");
  }
  let url: URL;
  try {
    url = new URL(value.url);
  } catch {
    throw new Error("invalid_mark_prompts_payload");
  }
  if (url.protocol !== "https:") {
    throw new Error("invalid_mark_prompts_payload");
  }
  return {
    kind: value.kind as MarkPromptLinkKind,
    label: value.label,
    url: url.toString(),
    status: value.status as MarkPromptLinkStatus,
    githubNumber: value.github_number,
    updatedAt: value.updated_at,
  };
}

function prompt(value: unknown): MarkPrompt {
  if (
    !object(value)
    || !exactKeys(value, [
      "id",
      "occurred_at",
      "summary",
      "abbreviated_text",
      "full_text",
      "review_state",
      "result_summary",
      "duplicate_of_prompt_id",
      "source",
      "links",
    ])
    || !boundedString(value.id, 160)
    || !timestamp(value.occurred_at)
    || !boundedString(value.summary, 240)
    || !boundedString(value.abbreviated_text, 1200)
    || !boundedString(value.full_text, 1_000_000)
    || typeof value.review_state !== "string"
    || !reviewStates.has(value.review_state as MarkPromptReviewState)
    || (
      value.result_summary !== null
      && !boundedString(value.result_summary, 2400)
    )
    || (
      value.duplicate_of_prompt_id !== null
      && !boundedString(value.duplicate_of_prompt_id, 160)
    )
    || (
      value.review_state === "duplicate"
      && value.duplicate_of_prompt_id === null
    )
    || (
      value.review_state !== "duplicate"
      && value.duplicate_of_prompt_id !== null
    )
    || !object(value.source)
    || !exactKeys(value.source, ["task_id", "task_title"])
    || !boundedString(value.source.task_id, 160)
    || !boundedString(value.source.task_title, 240)
    || !Array.isArray(value.links)
    || value.links.length > 30
  ) {
    throw new Error("invalid_mark_prompts_payload");
  }
  const parsedLinks = value.links.map(link);
  if (new Set(parsedLinks.map(({ url }) => url)).size !== parsedLinks.length) {
    throw new Error("invalid_mark_prompts_payload");
  }
  return {
    id: value.id,
    occurredAt: value.occurred_at,
    summary: value.summary,
    abbreviatedText: value.abbreviated_text,
    fullText: value.full_text,
    reviewState: value.review_state as MarkPromptReviewState,
    resultSummary: value.result_summary,
    duplicateOfPromptID: value.duplicate_of_prompt_id,
    source: {
      taskID: value.source.task_id,
      taskTitle: value.source.task_title,
    },
    links: parsedLinks,
  };
}

export function parseMarkPromptsPayload(value: unknown): MarkPromptsPageData {
  if (
    !object(value)
    || !exactKeys(value, [
      "schema_version",
      "stats",
      "sources",
      "order",
      "prompts",
      "next_offset",
    ])
    || value.schema_version !== "mark-prompts-public-v2"
    || !object(value.stats)
    || !exactKeys(value.stats, ["total", "by_day"])
    || typeof value.stats.total !== "number"
    || !Number.isSafeInteger(value.stats.total)
    || value.stats.total < 0
    || !Array.isArray(value.stats.by_day)
    || value.stats.by_day.length > 3660
    || !Array.isArray(value.sources)
    || value.sources.length > 1000
    || !["asc", "desc"].includes(String(value.order))
    || !Array.isArray(value.prompts)
    || value.prompts.length > 100
    || (
      value.next_offset !== null
      && (
        typeof value.next_offset !== "number"
        || !Number.isSafeInteger(value.next_offset)
        || value.next_offset < 1
      )
    )
  ) {
    throw new Error("invalid_mark_prompts_payload");
  }
  const byDay = value.stats.by_day.map((candidate) => {
    if (
      !object(candidate)
      || !exactKeys(candidate, ["date", "count"])
      || typeof candidate.date !== "string"
      || !/^\d{4}-\d{2}-\d{2}$/.test(candidate.date)
      || typeof candidate.count !== "number"
      || !Number.isSafeInteger(candidate.count)
      || candidate.count < 1
    ) {
      throw new Error("invalid_mark_prompts_payload");
    }
    return { date: candidate.date, count: candidate.count };
  });
  const prompts = value.prompts.map(prompt);
  if (new Set(prompts.map(({ id }) => id)).size !== prompts.length) {
    throw new Error("invalid_mark_prompts_payload");
  }
  const sources = value.sources.map((candidate) => {
    if (
      !object(candidate)
      || !exactKeys(candidate, [
        "task_id",
        "task_title",
        "count",
        "first_occurred_at",
        "last_occurred_at",
      ])
      || !boundedString(candidate.task_id, 160)
      || !boundedString(candidate.task_title, 240)
      || typeof candidate.count !== "number"
      || !Number.isSafeInteger(candidate.count)
      || candidate.count < 1
      || !timestamp(candidate.first_occurred_at)
      || !timestamp(candidate.last_occurred_at)
    ) {
      throw new Error("invalid_mark_prompts_payload");
    }
    return {
      taskID: candidate.task_id,
      taskTitle: candidate.task_title,
      count: candidate.count,
      firstOccurredAt: candidate.first_occurred_at,
      lastOccurredAt: candidate.last_occurred_at,
    };
  });
  if (
    new Set(sources.map(({ taskID }) => taskID)).size !== sources.length
  ) {
    throw new Error("invalid_mark_prompts_payload");
  }
  return {
    stats: { total: value.stats.total, byDay },
    sources,
    order: value.order as MarkPromptOrder,
    prompts,
    nextOffset: value.next_offset,
  };
}

async function jsonValue(response: Response): Promise<unknown> {
  const contentType = response.headers.get("content-type")?.toLowerCase() ?? "";
  if (!contentType.includes("application/json")) {
    throw new Error("unexpected_response");
  }
  return response.json();
}

export class CloudflareMarkPromptsRepository
  implements MarkPromptsRepository {
  async load(options: {
    offset?: number;
    limit?: number;
    taskIDs?: string[];
    order?: MarkPromptOrder;
  } = {}): Promise<MarkPromptsPageData> {
    const {
      offset = 0,
      limit = 50,
      taskIDs = [],
      order = "desc",
    } = options;
    const query = new URLSearchParams({
      limit: String(limit),
      offset: String(offset),
      order,
    });
    taskIDs.forEach((taskID) => query.append("task", taskID));
    let response: Response;
    try {
      response = await fetch(
        `${apiRoute("/public/mark-prompts")}?${query}`,
        {
          credentials: "same-origin",
          cache: "no-store",
        },
      );
    } catch {
      throw new MarkPromptsRepositoryError(null);
    }
    if (!response.ok) {
      throw new MarkPromptsRepositoryError(response.status);
    }
    try {
      return parseMarkPromptsPayload(await jsonValue(response));
    } catch {
      throw new MarkPromptsRepositoryError(response.status);
    }
  }
}
